import { useState, Suspense } from "react"
import { Box, CircularProgress } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { usePermissions } from "@crm-src/hooks/usePermissions"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { PeriodType } from "@crm-src/types/financial-metrics"
import { TimeRangeSelector } from "@crm-src/components/financial-metrics/time-range-selector"
import { PDFExportButton } from "@crm-src/components/financial-metrics/pdf-export-button"
import { PageHeader } from "@/components/ui/page-header"

function FinancialMetricsLayoutContent({
  children,
}: {
  children: React.ReactNode
}) {
  const { canAccessAdmin } = usePermissions()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isAdmin = canAccessAdmin()
  
  const [period, setPeriod] = useState<PeriodType>(
    (searchParams.get('period') as PeriodType) || '1M'
  )

  // Update URL when period changes
  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod)
    const currentPath = pathname
    navigate(`${currentPath}?period=${newPeriod}`)
  }

  // Show header and navigation for admin on sub-pages
  // But exclude individual salesperson detail pages (they have their own minimal header)
  const isSalespersonDetailPage = pathname.match(/\/financial-metrics\/salespeople\/\d+/)
  const isAdminSubPage = isAdmin && pathname !== '/financial-metrics' && !isSalespersonDetailPage
  
  if (!isAdminSubPage) {
    return <>{children}</>
  }

  const navLinks = [
    { href: `/crm/financial-metrics/company?period=${period}`, label: 'Company Overall', path: '/financial-metrics/company' },
    { href: `/crm/financial-metrics/salespeople?period=${period}`, label: 'Salespeople Performance', path: '/financial-metrics/salespeople' },
    { href: `/crm/financial-metrics/products?period=${period}`, label: 'Product Performance', path: '/financial-metrics/products' },
  ]

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Financial Metrics"
        description="Comprehensive financial insights and sales performance"
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TimeRangeSelector selected={period} onChange={handlePeriodChange} />
            <PDFExportButton period={period} variant="outline" />
          </Box>
        }
      />

      {/* Link-based Navigation */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 1,
          p: 1,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          borderRadius: 1,
        }}
      >
        {navLinks.map((link) => {
          // For salesperson detail pages, highlight "Salespeople Performance" tab
          const isSalespersonDetailPage = pathname.match(/\/financial-metrics\/salespeople\/\d+/)
          const isActive = isSalespersonDetailPage
            ? link.path === '/financial-metrics/salespeople'
            : pathname.startsWith(link.path)
          return (
            <Box
              key={link.path}
              component={Link}
              to={link.href}
              sx={{
                py: 2,
                px: 3,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 1,
                transition: "all 0.2s",
                textAlign: "center",
                textDecoration: "none",
                ...(isActive
                  ? { bgcolor: "background.paper", boxShadow: 1, color: "text.primary" }
                  : {
                      color: "text.secondary",
                      "&:hover": { color: "text.primary", bgcolor: (t) => alpha(t.palette.background.paper, 0.5) },
                    }),
              }}
            >
              {link.label}
            </Box>
          )
        })}
      </Box>

      {/* Page Content */}
      {children}
    </Box>
  )
}

export default function FinancialMetricsLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <Suspense fallback={
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} sx={{ color: "primary.main" }} />
      </Box>
    }>
      <FinancialMetricsLayoutContent>{children}</FinancialMetricsLayoutContent>
    </Suspense>
  )
}

